import { Component, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {Store} from '@ngxs/store';
import { BehaviorSubject, EMPTY, Observable } from 'rxjs';
import { catchError, finalize, map, startWith, switchMap, take, tap } from 'rxjs/operators';

import {
  AuthService,
  LoginEmailService,
  LoginFormType,
  QUERY_PARAM_LOGIN_FORM_TYPE,
  SendPasswordlessLoginEmailUserError,
  SetLoginFormType
} from '@aiwyn/auth';
import {canShowValidationErrors, Logger} from '@aiwyn/shared';
import {AssetUriService, ConfigService, ConfigState, LaunchDarklyFlag, LaunchDarklyService} from '@aiwyn/config';

import {ROUTE_TOKEN_LOGIN, ROUTE_TOKEN_MAIN_BASE} from '../../../routing-constants';

@Component({
  selector: 'app-login-passwordless-expired',
  templateUrl: './login-passwordless-expired.component.html',
  styleUrls: ['./login-passwordless-expired.component.scss'],
  standalone: false,
})
export class LoginPasswordlessExpiredComponent {
  readonly canShowValidationErrors = canShowValidationErrors;

  emailSignInForm = new UntypedFormGroup({ email: new UntypedFormControl('', [Validators.required, Validators.email]) });
  cachedEmailAddress$: Observable<string> = this.authService.getAuthEmail().pipe(
    map(email => email ? ` for ${email}` : ''),
    startWith(''),
  );
  isSubmissionProcessing$ = new BehaviorSubject<boolean>(false);

  customerName$ = this.configService.configValuesLoaded$.pipe(
    switchMap(_ => this.store.select(ConfigState.getCustomerName)),
  );

  enableMagicLinkExperience$ = this.launchDarklyService.getFeatureFlag(LaunchDarklyFlag.ENABLE_PASSWORDLESS_LOGIN);

  loginError$: Observable<boolean> = this.activatedRoute.queryParamMap.pipe(
    map(paramMap => !!paramMap?.get('loginError')),
  );
  userEmailError = signal(false);

  constructor(
    private readonly launchDarklyService: LaunchDarklyService,
    private readonly configService: ConfigService,
    private readonly logger: Logger,
    private readonly store: Store,
    private router: Router,
    public assetUriService: AssetUriService,
    public authService: AuthService,
    private readonly loginEmailService: LoginEmailService,
    private readonly activatedRoute: ActivatedRoute,
  ) {
    this.authService.getAuthEmail().pipe(
      tap(email => {
        if (email) {
          this.emailSignInForm.get('email').setValue(email);
        }
      }),
      take(1),
    ).subscribe();
  }

  signInWithPassword(): void {
    this.router.navigate(
      [ROUTE_TOKEN_LOGIN],
      { queryParams: { [QUERY_PARAM_LOGIN_FORM_TYPE]: LoginFormType.USERNAME_PASSWORD } }
    );
  }

  goBack(): void {
    this.authService.getUser(true).pipe(
      take(1),
      switchMap(user => this.router.navigate(user ? [ROUTE_TOKEN_MAIN_BASE, 'accountSettings'] : [ROUTE_TOKEN_LOGIN])),
    ).subscribe();
  }

  showGuestLogin(): boolean {
    return !this.configService.relaxedModeEnabled && !this.configService.bypassClientKey;
  }

  getClientKeyForAnonymousUser(): void {
    // Reset the login type, so if we end up coming back to this page, we essentially start from scratch
    this.store.dispatch(new SetLoginFormType(null));
    this.router.navigateByUrl('clientkey');
  }

  signInWithEmailLink(): void {
    this.userEmailError.set(false);
    this.emailSignInForm.markAllAsTouched();

    if (this.emailSignInForm.invalid) {
      this.logger.debug('AUTH : LoginPasswordlessExpiredComponent : signInWithEmailLink : Form invalid. Cannot submit.');
      return;
    }

    this.isSubmissionProcessing$.next(true);

    this.loginEmailService.sendPasswordlessLoginEmail(this.emailSignInForm.value.email).pipe(
      take(1),
      catchError(error => {
        if (error instanceof SendPasswordlessLoginEmailUserError) {
          this.userEmailError.set(true);
          return EMPTY;
        }

        throw error;
      }),
      finalize(() => this.isSubmissionProcessing$.next(false)),
    ).subscribe()
  }
}
